import { getSubmission } from '../api'
import { getSubmissions, saveSubmission } from '../db'
import { isAuthExpiredError } from '../authErrors'
import { dispatchUploadProgressEvent } from '../uploadEvents'
import type { Submission } from '@/types'
import { notifyRecordingChange } from './events'
import { waitMs } from './timing'

type SubmissionProcessingStatus = Awaited<ReturnType<typeof getSubmission>>

export async function refreshProcessingStatus(
  submissionId: string,
  serverItem?: SubmissionProcessingStatus,
): Promise<void> {
  try {
    const resolvedServerItem = serverItem ?? (await getSubmission(submissionId))
    const serverStatus = resolvedServerItem.processingStatus
    if (!serverStatus) return

    const localList = await getSubmissions()
    const local = localList.find((s) => s.id === submissionId)
    if (!local || serverStatus === local.processingStatus) return

    await saveSubmission({
      ...local,
      processingStatus: serverStatus as Submission['processingStatus'],
      processingUpdatedAt: resolvedServerItem.processingUpdatedAt ?? new Date().toISOString(),
      processingError: resolvedServerItem.processingError ?? null,
    })
    dispatchUploadProgressEvent({ type: 'refresh' })
    notifyRecordingChange()
  } catch (error) {
    if (isAuthExpiredError(error)) throw error
    // Non-fatal: background sweep will retry
  }
}

export async function pollProcessingStatus(
  submissionId: string,
  maxAttempts = 4,
  intervalMs = 1500,
): Promise<void> {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (attempt > 0) {
      await waitMs(intervalMs)
    }
    try {
      const serverItem = await getSubmission(submissionId)
      const status = serverItem.processingStatus
      if (status === 'completed' || status === 'failed') {
        await refreshProcessingStatus(submissionId, serverItem)
        return
      }
    } catch {
      return
    }
  }
}

export async function refreshAllStuckProcessingStatuses(): Promise<void> {
  const submissions = await getSubmissions()
  const stuck = submissions.filter(
    (s) => s.syncedAt && (s.processingStatus === 'queued' || s.processingStatus === 'processing'),
  )
  for (const s of stuck) {
    await refreshProcessingStatus(s.id)
  }
}
